import { LockClosedIcon } from '@heroicons/react/solid'
import { yupResolver } from '@hookform/resolvers/yup'
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { formatuseFormErrors, getErrorMessage, log } from '../../helpers/utils'
import { shouldSkipLoginRecaptcha } from '../../helpers/shouldSkipLoginRecaptcha'
import ErrorAlert from '../Alerts/ErrorAlert'
import { API } from '../../apis/API'
import { UserStore } from '../../state/UserStore'
import { useRef, useState } from 'react'
import Link from 'next/link'
import { mutate } from 'swr'
import { CustomerAPI } from '../../apis/CustomerAPI'
import Loader from '../Loader'
import ReCAPTCHA from 'react-google-recaptcha'

const loginFields = [
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'password', label: 'Password', type: 'password', required: true },
]

export const loginRules = () => {
    let rulesObject = {}
    loginFields.forEach(({ name }) => {
        rulesObject[name] = yup.string().required()
    })
    return yup.object().shape(rulesObject)
}

function LoginForm({
    onSuccess,
    submitLabel = 'Sign in',
    showCreateAccountLink = true,
    formClassName = 'hs-form-exclude space-y-4',
    labelClassName = 'mb-1.5 block text-[13px] font-medium text-gray-700',
    inputClassName = 'h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-[14px] text-[#0f1115] placeholder:text-gray-400 transition-all focus:border-[#ef6c2a] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#ef6c2a]/10',
    submitButtonClassName,
    forgotPasswordClassName = 'text-[14px] text-gray-500 transition hover:text-[#0f1115] hover:underline',
    forgotPasswordWrapperClassName = 'flex justify-center',
    createAccountText = 'Need an account?',
    createAccountLinkText = 'Create an Account',
    createAccountWrapperClassName = 'text-center text-[14px] text-gray-500',
    createAccountLinkClassName = 'font-semibold text-[#ef6c2a] hover:underline',
    showDivider = true,
}) {
    const skipCaptcha = shouldSkipLoginRecaptcha()
    const setUser = UserStore((state) => state.save)
    const updateCustomer = UserStore((state) => state.updateCustomer)
    const [loading, setLoading] = useState(false)
    const [captchaToken, setCaptchaToken] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const recaptchaRef = useRef()

    const onCaptchaChange = (value) => {
        setCaptchaToken(value || null)
        if (recaptchaRef.current) {
            recaptchaRef.current.value = value
        }
    }

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm({ resolver: yupResolver(loginRules()) })

    const attemptLogin = async (data) => {
        try {
            setLoading(true)
            if (!skipCaptcha && !captchaToken) {
                throw Error('Please complete the reCAPTCHA.')
            }

            const gReCaptchaToken = skipCaptcha ? '' : captchaToken
            const payload = {
                ...data,
                captcha: gReCaptchaToken,
            }

            const { data: user } = await API.login(payload)
            setUser(user)

            const customer = await mutate(CustomerAPI.getCustomer)
            if (customer && !customer?.unauthorized) {
                updateCustomer(customer)
            }

            await onSuccess?.({ user, customer })
        } catch (e) {
            const message = getErrorMessage(e)
            log('error', message)
            setError('custom', { type: 'custom', message })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {loading && <Loader />}
            <form
                data-hs-cf-disabled="true"
                onSubmit={handleSubmit(attemptLogin)}
                className={formClassName}
                method="POST">
                <input type="hidden" name="remember" defaultValue="true" />

                <div>
                    <label htmlFor="email" className={labelClassName}>
                        Email
                    </label>
                    <input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        {...register('email')}
                        className={inputClassName}
                    />
                </div>

                <div>
                    <label htmlFor="password" className={labelClassName}>
                        Password
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            {...register('password')}
                            className={`${inputClassName} pr-11`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                            }>
                            {showPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>

                {Object.values(errors).length > 0 && (
                    <ErrorAlert
                        error={formatuseFormErrors(errors)}
                        heading="Please fix following error(s)."
                    />
                )}

                {!skipCaptcha && (
                    <div>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={
                                process.env.NEXT_PUBLIC_RECAPTCHA2_SITE_KEY
                            }
                            onChange={onCaptchaChange}
                            onExpired={() => onCaptchaChange(null)}
                            size="normal"
                            theme="light"
                        />
                    </div>
                )}
                <div className={forgotPasswordWrapperClassName}>
                    <Link href="/reset-password">
                        <a className={forgotPasswordClassName}>
                            Forgot password?
                        </a>
                    </Link>
                </div>
                <button
                    type="submit"
                    disabled={(!skipCaptcha && !captchaToken) || loading}
                    className={
                        submitButtonClassName ||
                        `relative flex w-full justify-center rounded-xl py-3.5 text-[15px] font-semibold transition-all shadow-sm ${
                            (!skipCaptcha && !captchaToken) || loading
                                ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                                : 'bg-[#ef6c2a] text-white hover:-translate-y-0.5 hover:shadow-md'
                        }`
                    }>
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <LockClosedIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                        />
                    </span>
                    {submitLabel}
                </button>

                <div className={showDivider ? 'pt-2' : ''}>
                    {showCreateAccountLink && (
                        <>
                            {showDivider && (
                                <div className="my-4">
                                    <div className="relative">
                                        <div
                                            className="absolute inset-0 flex items-center"
                                            aria-hidden="true">
                                            <div className="w-full border-t border-gray-300" />
                                        </div>
                                        <div className="relative flex justify-center">
                                            <span className="bg-white px-2 text-sm text-gray-500">
                                                OR
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <p className={createAccountWrapperClassName}>
                                {createAccountText}{' '}
                                <Link href="/create-account">
                                    <a className={createAccountLinkClassName}>
                                        {createAccountLinkText}
                                    </a>
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </form>
        </>
    )
}

export default LoginForm
